"use client";

import { useMemo, useRef, useState, type JSX, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Search, X } from "lucide-react";

import { Icon } from "@repo/ui/icon";
import {
  searchClient,
  createTypeScopedSearchClient,
} from "@/lib/algolia/client";
import { getAlgoliaIndexName } from "@/lib/algolia/config";
import {
  ListingCard,
  ListingCardContainer,
  ListingCardSkeleton,
} from "@/components/cards/listing-card";
import { LISTING_TYPE_CONFIGS } from "@/components/directory-listings/directory-listing.config";
import {
  hitToListingItem,
  type DirectoryHit,
} from "@/components/directory-listings/algolia/hit-to-listing-item";
import type { Locale } from "@/i18n/config";

const PAGE_SIZE = 24;
const DEBOUNCE_MS = 250;
const SKELETON_COUNT = 8;

export interface ArticleSearchProps {
  locale: Locale;
  filter: string | undefined;
  columns?: 2 | 3 | 4;
  searchPlaceholderKey?: string;
  children: ReactNode;
}

export function ArticleSearch({
  locale,
  filter,
  columns = 3,
  searchPlaceholderKey = "inputPlaceholder",
  children,
}: ArticleSearchProps): JSX.Element {
  const t = useTranslations("search");
  const indexName = getAlgoliaIndexName(locale);
  const client = useMemo(
    () =>
      createTypeScopedSearchClient(searchClient, "article", PAGE_SIZE, filter),
    [filter],
  );
  const [value, setValue] = useState("");
  const [hits, setHits] = useState<DirectoryHit[] | null>(null);
  const [pending, setPending] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function search(next: string) {
    setValue(next);
    if (timer.current) clearTimeout(timer.current);
    if (!next.trim()) {
      setHits(null);
      setPending(false);
      return;
    }
    setPending(true);
    timer.current = setTimeout(async () => {
      try {
        const response = (await client.search([
          { indexName, params: { query: next } },
        ])) as { results: Array<{ hits?: DirectoryHit[] }> };
        setHits(response.results[0]?.hits ?? []);
      } catch {
        setHits([]);
      } finally {
        setPending(false);
      }
    }, DEBOUNCE_MS);
  }

  function clear() {
    setValue("");
    setHits(null);
    setPending(false);
    inputRef.current?.focus();
  }

  const isSearching = value.trim().length > 0;

  return (
    <>
      <div className="container pt-10 desktop:pt-14">
        <div
          role="search"
          className="flex items-center gap-3 border-b border-border-default pb-3"
        >
          <Icon
            name="search"
            size="md"
            className="shrink-0 text-text-primary"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="search"
            value={value}
            onChange={(event) => search(event.currentTarget.value)}
            placeholder={t(searchPlaceholderKey)}
            aria-label={t("inputAriaLabel")}
            className="w-full bg-transparent typography-body-md-regular text-text-primary placeholder:text-text-secondary focus:outline-none [&::-webkit-search-cancel-button]:appearance-none"
          />
          {value.length > 0 && (
            <button
              type="button"
              onClick={clear}
              aria-label={t("clearSearch")}
              className="shrink-0 cursor-pointer rounded-full bg-button-primary-background p-1 text-button-primary-text transition-opacity hover:opacity-80"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {!isSearching ? (
        children
      ) : pending ? (
        <section className="pb-16 pt-10 desktop:pb-24 desktop:pt-14">
          <ListingCardContainer columns={columns} carouselOnMobile={false}>
            {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <ListingCardSkeleton key={index} />
            ))}
          </ListingCardContainer>
        </section>
      ) : hits && hits.length > 0 ? (
        <section className="pb-16 pt-10 desktop:pb-24 desktop:pt-14">
          <ListingCardContainer columns={columns}>
            {hits.map((hit) => (
              <ListingCard
                key={hit.objectID}
                {...LISTING_TYPE_CONFIGS.articles.mapToCardProps(
                  hitToListingItem(hit, "articles"),
                )}
              />
            ))}
          </ListingCardContainer>
        </section>
      ) : (
        <section className="pb-16 pt-10 desktop:pb-24 desktop:pt-14">
          <div className="container flex flex-col items-center gap-6 py-16 text-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-surface-base-secondary">
              <Search className="size-8 text-text-secondary" />
            </div>
            <div className="flex flex-col gap-2">
              <h2 className="typography-body-lg-medium text-text-primary">
                {t("noResultsFoundFor", { query: value })}
              </h2>
              <p className="typography-body-md-regular text-text-secondary">
                {t("noResultsDescription")}
              </p>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
